import type { EnvironmentId, ThreadId } from "@cmmd-forge/contracts";
import type { ReactElement } from "react";
import { GitPullRequestIcon } from "lucide-react";
import { LockedTrigger } from "./ComposerContextFact";
import { RESPONSIVE_CONTROL_HIT_AREA_CLASS } from "~/lib/touchTarget";

import { cn } from "~/lib/utils";
import {
  BranchChip,
  type BranchChipProps,
  ExecutionChip,
  type ExecutionChipProps,
  WorktreeChip,
  type WorktreeChipProps,
} from "../newThread/composerChips";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";
import { STRIP_CONTROL_CLASS } from "./composerControlsStripChrome";
import { COMPOSER_FOCUS_RING_CLASS } from "./composerFocusRing";
import {
  ComposerControlsStripOverflow,
  type ComposerControlsStripOverflowProps,
} from "./ComposerControlsStripOverflowMenu";
import {
  buildChatComposerPrBadge,
  buildComposerControlsStripTriggers,
  shouldShowChatComposerContextStrip,
  type ChatComposerContextStripInput,
  type ChatComposerPrInput,
} from "./ComposerControlsStrip.logic";

/**
 * The overflow menu's contents, from the facts a thread already has in hand.
 *
 * A function rather than an object literal at the call site: the orchestrator
 * is at its file-size cap, and both entries are conditional, so spelling the
 * two ternaries out there costs space that has to be bought back somewhere
 * else in the file for every later change.
 */
export function buildComposerControlsStripOverflow(input: {
  readonly cmmdProjectId: string | null;
  readonly environmentId: EnvironmentId;
  readonly activeThreadId: ThreadId | null;
  readonly taskAnchorId: string | null;
  readonly taskAnchorDisabled: boolean;
  readonly showPlanSidebarToggle: boolean;
  readonly planSidebarLabel: string;
  readonly planSidebarOpen: boolean;
  readonly onTogglePlanSidebar: () => void;
  readonly goal: ComposerControlsStripOverflowProps["goal"];
}): ComposerControlsStripOverflowProps {
  return {
    // A saved thread on a numeric CMMD project id are the two things the picker
    // needs before it can list or attach a task.
    taskAnchor:
      input.cmmdProjectId !== null && input.activeThreadId !== null
        ? {
            cmmdProjectId: input.cmmdProjectId,
            environmentId: input.environmentId,
            threadId: input.activeThreadId,
            currentTaskId: input.taskAnchorId,
            disabled: input.taskAnchorDisabled,
          }
        : null,
    planSidebar: input.showPlanSidebarToggle
      ? {
          label: input.planSidebarLabel,
          open: input.planSidebarOpen,
          onToggle: input.onTogglePlanSidebar,
        }
      : null,
    goal: input.goal ?? null,
  };
}

/**
 * The strip an existing thread shows, or nothing when there is no run context
 * worth a strip.
 *
 * A function rather than a conditional spread at the call site for the same
 * reason `buildComposerControlsStripOverflow` is one: the orchestrator is at
 * its file-size cap, and a nested ternary around a twenty line element costs
 * space that every later change has to buy back somewhere else in that file.
 */
export function buildChatComposerContextStrip(
  input: Parameters<typeof buildComposerControlsStripOverflow>[0] & {
    readonly facts: ChatComposerContextStripInput;
    readonly pullRequest: ChatComposerPrInput;
    readonly controlsHostRef: (element: HTMLDivElement | null) => void;
    readonly forceControlsHost?: boolean;
  },
): ReactElement | undefined {
  if (
    !shouldShowChatComposerContextStrip(input.facts) &&
    !input.forceControlsHost &&
    input.goal == null
  )
    return undefined;
  return (
    <ComposerControlsStrip
      mode="thread"
      facts={input.facts}
      pullRequest={input.pullRequest}
      controlsHostRef={input.controlsHostRef}
      overflow={buildComposerControlsStripOverflow(input)}
    />
  );
}

interface ComposerControlsStripSharedProps {
  /**
   * The portal host's element, handed back to whoever wants to render into it.
   *
   * t3code calls this `composerControlsHostRef`. It stays empty in this card:
   * the Resting Composer portals the footer's model, effort and access controls
   * into it, and the host has to exist and be measurable before that lands or
   * the first measurement happens after the controls are already visible.
   */
  readonly controlsHostRef?: ((element: HTMLDivElement | null) => void) | undefined;
  readonly overflow?: ComposerControlsStripOverflowProps | undefined;
}

/**
 * New-thread landing: the three controls are live and bind to the Draft hooks
 * the landing already owns. The strip renders the same pickers the chips
 * rendered before, so there is exactly one owner of each choice.
 */
interface ComposerControlsStripLandingProps extends ComposerControlsStripSharedProps {
  readonly mode: "landing";
  readonly execution: ExecutionChipProps;
  readonly worktree: WorktreeChipProps;
  readonly branch: BranchChipProps;
}

/**
 * Existing thread: the same three render as non-interactive triggers.
 *
 * Not pickers, because Forge has no server command to move a running thread's
 * Environment, worktree or branch. A trigger that opens a menu it cannot act on
 * promises an interaction that does not exist, so these carry the value and
 * refuse the click instead.
 */
interface ComposerControlsStripThreadProps extends ComposerControlsStripSharedProps {
  readonly mode: "thread";
  readonly facts: ChatComposerContextStripInput;
  readonly pullRequest?: ChatComposerPrInput | undefined;
}

export type ComposerControlsStripProps =
  | ComposerControlsStripLandingProps
  | ComposerControlsStripThreadProps;

/**
 * The controls strip welded under the Composer.
 *
 * Left to right: the Environment, the worktree mode, the portal host, then the
 * branch at the far right. One component for both modes on purpose. The strip
 * is the thing a person looks at across the new-thread to thread transition,
 * and two components is two places for the order, the chrome and the vocabulary
 * to drift apart.
 *
 * No visibility or padding of its own: `ComposerSurface`'s strip slot owns
 * both, so this cannot disagree with the clip-path that shapes the strip
 * around it.
 */
export function ComposerControlsStrip(props: ComposerControlsStripProps): ReactElement {
  return (
    <div
      aria-label="Thread context"
      // Flat row of siblings — no grouping wrapper. Every control is
      // `shrink-0`, so an intermediate shrinkable group collapses below its
      // content and the controls collide instead of scrolling.
      // `NewThreadLanding.ssr.test.tsx` guards this.
      className="turn-chip-strip flex w-full items-center gap-1 overflow-x-auto"
      data-composer-context-control="true"
      data-composer-controls-strip="true"
    >
      {props.mode === "landing" ? (
        <>
          <ExecutionChip {...props.execution} triggerClassName={STRIP_CONTROL_CLASS} />
          <WorktreeChip {...props.worktree} triggerClassName={STRIP_CONTROL_CLASS} />
          <ControlsHost hostRef={props.controlsHostRef} />
          <ComposerControlsStripOverflow overflow={props.overflow} />
          <BranchChip {...props.branch} triggerClassName={STRIP_CONTROL_CLASS} />
        </>
      ) : (
        <ThreadTriggers {...props} />
      )}
    </div>
  );
}

/**
 * The empty box the Resting Composer portals its controls into.
 *
 * `flex-1` rather than an auto margin on the branch: the host takes whatever
 * the Environment, worktree and branch controls leave over, which is what lets
 * a Resting Composer show its model and access controls wherever they fit. It
 * also puts the branch at the right-hand end without a margin that would
 * collapse the moment the row overflows.
 */
function ControlsHost({
  hostRef,
}: {
  readonly hostRef: ((element: HTMLDivElement | null) => void) | undefined;
}): ReactElement {
  return (
    <div
      ref={hostRef ?? null}
      data-composer-controls-host="true"
      className="flex min-w-0 flex-1 items-center justify-start overflow-x-clip overflow-y-visible"
    />
  );
}

function ThreadTriggers(props: ComposerControlsStripThreadProps): ReactElement {
  const triggers = buildComposerControlsStripTriggers(props.facts);
  const pr = props.pullRequest ? buildChatComposerPrBadge(props.pullRequest) : null;

  // The badge is rendered immediately before the branch, because it is about
  // *that* branch; splitting them across the strip would read as two unrelated
  // facts. DOM order does this rather than `order:`, which would also reorder
  // them for the keyboard and for a screen reader.
  const leading = triggers.filter((trigger) => trigger.id !== "branch");
  const branch = triggers.find((trigger) => trigger.id === "branch") ?? null;

  return (
    <>
      {leading.map((trigger) => (
        <LockedTrigger key={trigger.id} trigger={trigger} />
      ))}
      <ControlsHost hostRef={props.controlsHostRef} />
      <ComposerControlsStripOverflow overflow={props.overflow} />
      {pr ? (
        <Tooltip>
          <TooltipTrigger
            render={
              <a
                href={pr.url}
                target="_blank"
                rel="noreferrer"
                aria-label={pr.label}
                data-chat-composer-pr-badge={pr.number}
                className={cn(
                  COMPOSER_FOCUS_RING_CLASS,
                  RESPONSIVE_CONTROL_HIT_AREA_CLASS,
                  "inline-flex shrink-0 items-center gap-1 rounded px-1 font-medium text-sm tabular-nums transition-colors hover:bg-muted/60 md:h-6 md:min-h-0 md:min-w-0 md:text-[11px]",
                  pr.colorClass,
                )}
              />
            }
          >
            <GitPullRequestIcon aria-hidden="true" className="size-3 shrink-0" />
            <span>#{pr.number}</span>
          </TooltipTrigger>
          <TooltipPopup side="top">{pr.tooltip}</TooltipPopup>
        </Tooltip>
      ) : null}
      {branch ? <LockedTrigger trigger={branch} /> : null}
    </>
  );
}
